/**
 * Tag-side compareTo resolution — turns a metric condition's `compareTo`
 * into the threshold `compareMetric` receives. NaN whenever the RHS window
 * has no data for the item (fail-closed: a dataless window must never read
 * as zero) or the window is invalid; the existing finite-guard in
 * `compareMetric` then rejects the condition, `!=` included.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  compareAgainstTotal,
  compareThreshold,
  resolveCompareWindow,
  tagCompareWindow,
  type TagCompareTo,
} from "../flow-optimizer-compare-to";
import { totalFlowMetrics, type MetricBag } from "../tag-rules/metric-totals";
import { flowTagRuleCubeKey, type FlowTagRuleWindow } from "./cube-key";
import { pickMetrics, readMetricField } from "./metrics";
import type { FlowTagItem } from "./types";

/** Population TOTAL for (this item's layer, cubeKey); undefined = no data. */
export type FlowTagTotals = (
  item: FlowTagItem,
  cubeKey: string | undefined,
) => MetricBag | undefined;

/**
 * Lazily memoized layer-scoped totals over the evaluated items — the
 * blended TOTAL of the table the rule looks at (raws summed via the FO
 * vocabulary, ratios derived once). Same `pickMetrics` fallback chain as
 * the element side, so an inheriting window totals the same window the LHS
 * reads.
 */
export function buildFlowTagTotals(
  items: ReadonlyArray<FlowTagItem>,
): FlowTagTotals {
  const cache = new Map<string, MetricBag | null>();
  return (item, cubeKey) => {
    const layer = item.layer ?? "";
    const key = `${layer}|${cubeKey ?? ""}`;
    if (!cache.has(key)) {
      const bags = items
        .filter((it) => (it.layer ?? "") === layer)
        .map((it) => pickMetrics(it, cubeKey));
      cache.set(key, totalFlowMetrics(bags) ?? null);
    }
    return cache.get(key) ?? undefined;
  };
}

/** RHS value for one window; NaN when the bag is empty or the field absent. */
export function flowTagCompareValue(
  item: FlowTagItem,
  field: string,
  cubeKey?: string,
): number {
  const bag = pickMetrics(item, cubeKey);
  if (Object.keys(bag).length === 0) return Number.NaN;
  return readMetricField(bag, field) ?? Number.NaN;
}

/**
 * The compare threshold for a condition on `rule`: the RHS metric over its
 * own window (or the RULE's window when none is given), × percent/100.
 */
export function tagCompareThreshold(
  item: FlowTagItem,
  compareTo: TagCompareTo,
  rule: FlowTagRuleWindow,
  totals?: FlowTagTotals,
): number {
  const win = resolveCompareWindow(tagCompareWindow(compareTo));
  if (win.kind === "invalid") return Number.NaN;
  const cubeKey = win.kind === "key" ? win.cubeKey : flowTagRuleCubeKey(rule);
  if (compareAgainstTotal(compareTo)) {
    // The blended population TOTAL; no totals accessor ⇒ fail-closed.
    const bag = totals?.(item, cubeKey);
    if (!bag) return Number.NaN;
    return compareThreshold(
      readMetricField(bag, compareTo.field) ?? Number.NaN,
      compareTo.percent,
    );
  }
  return compareThreshold(
    flowTagCompareValue(item, compareTo.field, cubeKey),
    compareTo.percent,
  );
}
